import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useSelectFieldChrome } from './fieldChrome';
import { optionsFromChildren } from './optionsFromChildren';
import { DEFAULT_MAX_OPTIONS } from './AppSelect';

/**
 * Drop-in replacement for a native `<select>` / `Form.Select`: searchable, and its option
 * text wraps.
 *
 * `onChange` is handed a synthetic `{ target: { value } }` so existing
 * `(e) => set(field, e.target.value)` handlers keep working verbatim — which is also why
 * swapping the tag cannot change what any field persists. Options come from the `<option>`
 * children, so a call site keeps its own markup, including `.map()`-built lists.
 *
 * Shares `useSelectFieldChrome` with AppSelect rather than configuring react-select
 * separately. That is what gives it, for free and already tested: the option cap that
 * TomSelect used to provide, and the aria-describedby composition without which an error
 * message is never announced (react-select overwrites the plain prop).
 */
const FormFieldSelect = ({
  value,
  onChange,
  children,
  disabled = false,
  isLoading = false,
  placeholder,
  /** Renders the invalid state, and links the message to the input for screen readers. */
  error = null,
  maxOptions = DEFAULT_MAX_OPTIONS,
  /** Wrapper class — `app-select--sm` for rows of `form-control-sm` siblings. */
  className = '',
  style,
  name,
  // Injected by FG. react-select puts an `id` on its container, so the label has to
  // point at its inner input via inputId instead.
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}) => {
  const { inputId, selectComponents, styles, screenReaderStatus } = useSelectFieldChrome({
    id, error, ariaDescribedBy, required: false, maxOptions,
  });

  const options = optionsFromChildren(children);
  // The conventional leading "-- Select --" entry becomes the placeholder, and stays in
  // the list so it is still possible to clear the field the way the native select allowed.
  const blank = options.find((option) => option.value === '');
  const selected = options.find((option) => option.value === String(value ?? '')) || null;

  const control = (
    <Select
      classNamePrefix="app-select"
      styles={styles}
      components={selectComponents}
      screenReaderStatus={screenReaderStatus}
      value={value === '' || value === undefined || value === null
        ? null
        : (selected || { value, label: value })}
      options={options}
      onChange={(option) => onChange({ target: { value: option?.value ?? '' } })}
      isDisabled={disabled}
      isLoading={isLoading}
      placeholder={placeholder || blank?.label || 'Select…'}
      inputId={inputId}
      name={name}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      menuPlacement="auto"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
    />
  );

  // Only wrap when there is something to wrap for: the audit form places this component
  // inside its own field markup, and an unconditional extra div would shift that layout.
  if (!className && !style && !error) return control;

  return (
    <div className={className} style={style}>
      {control}
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

FormFieldSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  maxOptions: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

export default FormFieldSelect;
